import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PrefsProvider, useI18n } from "@/context/PrefsContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Leads from "@/pages/Leads";
import Estimates from "@/pages/Estimates";
import Schedule from "@/pages/Schedule";
import Services from "@/pages/Services";
import Invoices from "@/pages/Invoices";
import Payments from "@/pages/Payments";
import Employees from "@/pages/Employees";
import EmployeeDetails from "@/pages/EmployeeDetails";
import Payroll from "@/pages/Payroll";
import Reviews from "@/pages/Reviews";
import Reports from "@/pages/Reports";
import SettingsPage from "@/pages/Settings";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F4FA]">
      <div className="w-10 h-10 border-4 border-[#5A189A]/20 border-t-[#5A189A] rounded-full animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function ThemeApplier() {
  const { theme } = useI18n();
  const location = useLocation();
  useEffect(() => {
    const root = document.documentElement;
    const isLogin = location.pathname.startsWith("/login");
    // Login screen is always light, regardless of the saved theme.
    if (theme === "dark" && !isLogin) root.classList.add("dark");
    else root.classList.remove("dark");
  }, [theme, location.pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <PrefsProvider>
        <AuthProvider>
          <BrowserRouter>
            <ThemeApplier />
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/clients" element={<Protected><Clients /></Protected>} />
            <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
            <Route path="/leads" element={<Protected><Leads /></Protected>} />
            <Route path="/estimates" element={<Protected><Estimates /></Protected>} />
            <Route path="/schedule" element={<Protected><Schedule /></Protected>} />
            <Route path="/services" element={<Protected><Services /></Protected>} />
            <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
            <Route path="/payments" element={<Protected><Payments /></Protected>} />
            <Route path="/employees" element={<Protected><Employees /></Protected>} />
            <Route path="/employees/:id" element={<Protected><EmployeeDetails /></Protected>} />
            <Route path="/payroll" element={<Protected><Payroll /></Protected>} />
            <Route path="/reviews" element={<Protected><Reviews /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </AuthProvider>
      </PrefsProvider>
    </div>
  );
}

export default App;
